import { type MantineColor, seriesColorAt } from '#core/lib/formatters/colors.ts';

/**
 * Override map for {@link categoryColorScale}: pins specific category values to
 * explicit palette tokens. Categories present here keep their token regardless
 * of data order; everything else cycles the shared series palette.
 */
export type CategoryColorOverrides = Readonly<Record<string, MantineColor>>;

/**
 * Stable category → color-token mapping for data-driven categorical charts
 * (donut, treemap, …). Shared SSOT so the same category always renders the
 * same color within a single chart, and every categorical chart draws from the
 * same palette as the cartesian series.
 *
 * Tokens are assigned in DATA ORDER: the first distinct category takes the
 * first palette slot, the second the next, and so on, cycling the shared
 * {@link SERIES_COLORS} palette (via {@link seriesColorAt}) so the assignment
 * never runs out of colors. An explicit override always wins and does NOT
 * consume a palette slot — order-assigned categories cycle the palette
 * independently of how many overrides are present.
 *
 * Duplicate category values collapse to a single slot (first occurrence wins),
 * so repeated rows never desync the color of a category.
 *
 * Resolve a returned token to a CSS color with `mantineColorVar(token)` from
 * `colors.ts` (e.g. for a recharts `<Cell fill={mantineColorVar(token)} />`).
 *
 * @param categories ordered category values (rendering order)
 * @param overrides optional category → token pins; these win over order
 * @returns a map from each distinct category to its resolved color token
 */
export function categoryColorScale(
  categories: readonly string[],
  overrides: CategoryColorOverrides = {},
): Map<string, MantineColor> {
  const distinct = categories.filter((category, index) => categories.indexOf(category) === index);

  return distinct.reduce<{ map: Map<string, MantineColor>; cursor: number }>(
    (acc, category) => {
      const override = overrides[category];
      if (override !== undefined) {
        acc.map.set(category, override);
        return acc;
      }
      acc.map.set(category, seriesColorAt(acc.cursor));
      return { map: acc.map, cursor: acc.cursor + 1 };
    },
    { map: new Map<string, MantineColor>(), cursor: 0 },
  ).map;
}
